import React, { useState, useEffect } from 'react';
import {
    Modal, Select, Form, Card, Avatar, Tag, Button, Space, message
} from 'antd';
import {
    UserOutlined, TeamOutlined, SwapOutlined,
    PhoneOutlined, MailOutlined
} from '@ant-design/icons';
import API from '../../../API/API';

const { Option } = Select;

const TransferLeadModal = ({
    visible,
    onCancel,
    onTransfer,
    lead,
    currentAgentId,
    agentMap: initialAgentMap
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [agents, setAgents] = useState([]);
    const [filteredAgents, setFilteredAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);

    useEffect(() => {
        if (visible) {
            loadAllAgents();
            form.resetFields();
            setSelectedAgent(null);
        }
    }, [visible]);

    const loadAllAgents = async () => {
        try {
            const response = await API.userGetAll();
            if (response?.success) {
                const allAgents = response.data
                // .filter(user =>
                //     user.role === "SALES-AGENT" && user.isActive
                // );
                setAgents(allAgents);
                filterAgentsByTierType(allAgents);
            }
        } catch (error) {
            console.error('Error loading agents:', error);
            message.error('Failed to load agents');
        }
    };


    const filterAgentsByTierType = (allAgents) => {
        const currentAgent = currentAgentId?._id || currentAgentId;

        // Filter: Only SALES-AGENT role, exclude current agent and logged in user
        const filtered = allAgents.filter(agent => {
            // Only show SALES-AGENT role
            if (agent.role !== "SALES-AGENT") {
                return false;
            }

            // Exclude logged in user (can't transfer to yourself)
            if (agent._id === loggedInUserId) {
                return false;
            }

            // Exclude current lead owner
            if (agent._id === currentAgent) {
                return false;
            }

            // Include all other sales agents
            return true;
        });

        setFilteredAgents(filtered);
    };

    // ────────────────────────────────────────────────
    //     THIS IS THE IMPORTANT PART — CONDITIONAL API
    // ────────────────────────────────────────────────
    const handleTransfer = async (values) => {
        if (!values.agentId) {
            message.warning('Please select an agent');
            return;
        }

        setLoading(true);
        try {
            const hide = message.loading('Transferring lead...', 0);

            let response;
            const isQueued = lead?.status === "queued" || lead?.queueLead === true;

            if (isQueued) {
                // Queued lead → use the special queue transfer API
                response = await API.transferQueuedLead(lead._id, {
                    targetAgentId: values.agentId,
                    reason: "Manual transfer"   // or values.reason if you add a reason field
                });
            } else {
                // Assigned / normal lead → use existing UpdateLead
                response = await API.UpdateLead(lead._id, {
                    handledBy: values.agentId
                });
            }

            hide();

            if (response?.success || response?.status) {
                message.success('Lead transferred successfully!');
                onTransfer(response.data || lead);
                onCancel();  // close modal
            } else {
                message.error(response?.message || response?.error || 'Transfer failed');
            }
        } catch (error) {
            console.error('Transfer error:', error);
            const serverMsg = error.response?.data?.message || error.message;
            message.error(serverMsg || 'Failed to transfer lead');
        } finally {
            setLoading(false);
        }
    };

    const getTierTypeColor = (tierType) => {
        switch (tierType) {
            case 'TIER-1': return 'green';
            case 'TIER-2': return 'blue';
            case 'TIER-3': return 'purple';
            default: return 'default';
        }
    };

    const getSubRoleColor = (subRole) => {
        switch (subRole) {
            case 'TIER-1': return 'success';
            case 'TIER2': return 'processing';
            case 'TIER3': return 'warning';
            default: return 'default';
        }
    };
    const loggedInUserId = JSON.parse(localStorage.getItem('userData') || '{}')?._id || null;
    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SwapOutlined />
                    <span>Transfer Lead</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={800}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="transfer"
                    type="primary"
                    loading={loading}
                    onClick={() => form.submit()}
                    icon={<SwapOutlined />}
                >
                    Transfer Lead
                </Button>
            ]}
        >
            <Form form={form} layout="vertical" onFinish={handleTransfer}>
                <Form.Item
                    label="Select Sales Agent"
                    name="agentId"
                    rules={[{ required: true, message: 'Please select an agent to transfer to' }]}
                >
                    <Select
                        placeholder="Search and select sales agent"
                        showSearch
                        filterOption={(input, option) =>
                            option.children.props.children[1].props.children[0].toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) => {
                            const agent = filteredAgents?.find(a => a?._id === value);
                            setSelectedAgent(agent);
                        }}
                        allowClear
                        style={{ width: '100%' }}
                        optionLabelProp="label"
                    >
                        {filteredAgents.map((agent) => {
                            // Determine online/offline status
                            const isOnline =
                                agent.isActive;
                            const isLoggedInUser = agent?._id === loggedInUserId;
                            const isCurrentLeadAgent = agent?._id === currentAgentId?._id;



                            const statusColor = isOnline ? '#52c41a' : '#ff4d4f'; // green or red
                            const statusText = isOnline ? 'Online' : 'Offline';

                            return (
                                <Option
                                    key={agent?._id}
                                    value={agent?._id}
                                    label={`${agent?.firstName} ${agent?.lastName}`}
                                    disabled={isLoggedInUser || isCurrentLeadAgent}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {/* Avatar */}
                                        <Avatar size="small">
                                            {agent.firstName?.charAt(0)}
                                        </Avatar>

                                        {/* Name + Status Dot */}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span>{agent.firstName} {agent.lastName}</span>
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: statusColor,
                                                        display: 'inline-block',
                                                        boxShadow: `0 0 4px ${statusColor}`
                                                    }}
                                                    title={statusText}
                                                />
                                            </div>

                                            {/* Extra info: phone + tier */}
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                {agent.directNumber && (
                                                    <span>
                                                        <PhoneOutlined /> {agent.directNumber}
                                                    </span>
                                                )}
                                                {agent.subRole && (
                                                    <Tag color={getSubRoleColor(agent.subRole)} style={{ marginLeft: 8, fontSize: 10 }}>
                                                        {agent.subRole}
                                                    </Tag>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TransferLeadModal;